// src/App.js
import React from 'react';
import { Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import AppProviders from './contexts/AppProviders';
import AppLayout from './layouts/AppLayout';
import HomePage from './pages/HomePage';
import SheetsPage from './pages/SheetsPage';
import SetlistsPage from './pages/SetlistsPage';
import NewSheetPage from './pages/NewSheetPage';
import NewSetlistPage from './pages/NewSetlistPage';
import SheetEditorPage from './pages/SheetEditorPage';
import SetlistViewPage from './pages/SetlistViewPage';
import BandSheetEditor from './components/BandSheetEditor';
import SharedSetlistView from './components/SharedSetlistView';
import PrintView from './components/PrintView';
import GlobalAuthModal from './components/Auth/GlobalAuthModal';
import Notification from './components/Notification';
import PersistentMediaPlayer from './components/PersistentMediaPlayer';
import BandsPage from './pages/BandsPage';
import BandPage from './pages/BandPage';
import ProfilePage from './pages/ProfilePage';
import SignInPage from './pages/SignInPage';
import LineupPage from './pages/LineupPage';
import EventsPage from './pages/EventsPage';
import EventPage from './pages/EventPage';
import InviteAcceptance from './components/InviteAcceptance';
import PublicRSVPPage from './pages/PublicRSVPPage';

// SheetEditor component with ID parameter
// Using a key based on sheetId forces component to remount when sheet changes
// This is critical for correct navigation behavior
const SheetEditorWithId = () => {
  const { sheetId } = useParams();
  return <BandSheetEditor initialSheetId={sheetId} key={`sheet-${sheetId}`} />;
};

// PrintView component with ID parameter for perform mode
const PrintViewWithId = () => {
  const { sheetId } = useParams();
  return <PrintView key={`print-${sheetId}`} />;
};

// Wrapper for PersistentMediaPlayer to work outside Routes
const MediaPlayerWrapper = () => {
  return <PersistentMediaPlayer />;
};

// Inner App component that has access to router context
function AppWithRouter() {
  const [searchParams] = useSearchParams();
  const isPrintMode = searchParams.get('perform') === 'true';
  
  // If in perform mode, render the print view without the layout
  if (isPrintMode) {
    return (
      <>
        <Routes>
          <Route path="/" element={<PrintView />} />
          <Route path="/sheet/:sheetId" element={<PrintViewWithId />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Notification />
      </>
    );
  }
  
  // Normal mode with full layout
  return (
    <>
      {/* Add GlobalAuthModal outside of Routes but inside AppProviders 
          to ensure it can be triggered from anywhere */}
      <GlobalAuthModal />
      <Notification />
      
      <Routes>
        <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
        <Route path="/sheets" element={<AppLayout><SheetsPage /></AppLayout>} />
        <Route path="/setlists" element={<AppLayout><SetlistsPage /></AppLayout>} />
        <Route path="/bands" element={<AppLayout><BandsPage /></AppLayout>} />
        <Route path="/band/:bandId" element={<AppLayout><BandPage /></AppLayout>} />
        <Route path="/band/:bandId/lineup/:lineupId" element={<AppLayout><LineupPage /></AppLayout>} />
        <Route path="/events" element={<AppLayout><EventsPage /></AppLayout>} />
        <Route path="/event/:eventId" element={<AppLayout><EventPage /></AppLayout>} />
        {/* Legacy redirect */}
        <Route path="/calendar" element={<Navigate to="/events" replace />} />
        <Route path="/profile" element={<AppLayout><ProfilePage /></AppLayout>} />
        <Route path="/profile/:userId" element={<AppLayout><ProfilePage /></AppLayout>} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/sheet/new" element={<AppLayout><NewSheetPage /></AppLayout>} />
        <Route path="/sheet/:sheetId" element={<AppLayout><SheetEditorPage /></AppLayout>} />
        <Route path="/setlist/new" element={<AppLayout><NewSetlistPage /></AppLayout>} />
        <Route path="/setlist/:id" element={<AppLayout><SetlistViewPage /></AppLayout>} />
        <Route path="/invite/:token" element={<InviteAcceptance />} />
        <Route path="/rsvp/:token" element={<PublicRSVPPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// Main App component
function App() {
  // Force cache bust: v5-Aug10-2025
  console.log('App loaded - Cache bust v5:', new Date().toISOString());
  
  return (
    <AppProviders>
      <AppWithRouter />
      {/* PersistentMediaPlayer outside Router context for consistent positioning */}
      <PersistentMediaPlayer />
    </AppProviders>
  );
}

export default App;
